/**
 * EnviroData/GIS API Client
 * Manages client's EnviroData/GIS integration for auto-populating plume data
 * and training client-specific XGBoost models
 */

import { apiClient } from './client';

// ============================================================================
// TYPES
// ============================================================================

export interface EnviroDataConfig {
  api_key: string;
  api_secret?: string;
  base_url?: string;
}

export interface GISConfig {
  api_key: string;
  api_secret?: string;
  base_url: string;
  database_type?: string;
  db_host?: string;
  db_port?: number;
  db_name?: string;
  db_user?: string;
  db_password?: string;
}

export interface IntegrationConfigRequest {
  envirodata?: EnviroDataConfig;
  gis?: GISConfig;
}

export interface EnviroDataConfigResponse {
  envirodata_configured: boolean;
  envirodata_base_url?: string;
  envirodata_api_key_preview?: string;
  gis_configured: boolean;
  gis_base_url?: string;
  gis_database_type?: string;
  gis_api_key_preview?: string;
  // Model training status
  has_trained_model: boolean;
  model_version?: string;
  model_trained_at?: string;
  model_metrics?: Record<string, number>;
}

// For backwards compatibility
export interface LegacyConfigResponse {
  configured: boolean;
  base_url?: string;
  gis_configured: boolean;
  gis_base_url?: string;
  api_key_preview?: string;
}

export interface TrainingDataSite {
  site_id: string;
  latitude: number;
  longitude: number;
  contaminant_type: string;
  measurements: Array<{
    date: string;
    plume_area_sqft?: number;
    concentration_ug_l?: number;
    plume_length_ft?: number;
    plume_width_ft?: number;
  }>;
  hydraulic_conductivity?: number;
  porosity?: number;
  hydraulic_gradient?: number;
}

export interface ModelTrainingRequest {
  fetch_from_gis?: boolean;
  site_ids?: string[];
  min_measurements?: number;
  training_data?: TrainingDataSite[];
}

export interface ModelTrainingResponse {
  success: boolean;
  message: string;
  job_id?: string;
  status: string;
}

export interface ModelStatusResponse {
  has_model: boolean;
  model_version?: string;
  trained_at?: string;
  training_sites: number;
  training_records: number;
  metrics?: Record<string, number>;
  status: string;
}

export interface TrainingStatusResponse {
  job_id?: string;
  status: string;
  progress: number;
  message: string;
  started_at?: string;
  updated_at?: string;
}

export interface PlumeDataRequest {
  latitude: number;
  longitude: number;
}

export interface WellData {
  well_id: string;
  latitude: number;
  longitude: number;
  well_type: string;
  depth_ft?: number;
  screen_top_ft?: number;
  screen_bottom_ft?: number;
  latest_concentration_ug_l?: number;
  sample_date?: string;
}

export interface PSHData {
  psh_present: boolean;
  thickness_ft?: number;
  area_sqft?: number;
  volume_gallons?: number;
  product_type?: string;
  recovery_rate_gal_day?: number;
  total_recovered_gallons?: number;
  mobility?: string;
  first_detected?: string;
}

export interface PlumeDataResponse {
  // Site identification
  site_id?: string;
  site_name?: string;
  facility_name?: string;
  facility_type?: string;
  regulatory_id?: string;

  // Location
  latitude: number;
  longitude: number;
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;

  // Contaminant data
  contaminant_type?: string;
  contaminants?: string[];
  max_concentration_mg_l?: number;
  max_concentration_ug_l?: number;
  benzene_concentration_ug_l?: number;
  toluene_concentration_ug_l?: number;
  ethylbenzene_concentration_ug_l?: number;
  xylenes_concentration_ug_l?: number;
  mtbe_concentration_ug_l?: number;
  tph_concentration_mg_l?: number;

  // Waste characterization data (for Precept)
  waste_type?: string;
  waste_source?: string;
  generator_name?: string;

  // TCLP Results (mg/L leachate)
  tclp_results?: {
    arsenic?: number;
    barium?: number;
    cadmium?: number;
    chromium?: number;
    lead?: number;
    mercury?: number;
    selenium?: number;
    silver?: number;
    benzene?: number;
    carbon_tetrachloride?: number;
    chloroform?: number;
    vinyl_chloride?: number;
    tetrachloroethylene?: number;
    trichloroethylene?: number;
    methyl_ethyl_ketone?: number;
    pyridine?: number;
  };

  // BTEX in soil (mg/kg)
  btex_soil?: {
    benzene_mg_kg?: number;
    toluene_mg_kg?: number;
    ethylbenzene_mg_kg?: number;
    xylenes_mg_kg?: number;
  };

  // Salinity parameters
  chloride_mg_kg?: number;
  electrical_conductivity_ds_m?: number;
  sar?: number;
  tds_mg_l?: number;

  // Physical properties
  flash_point_f?: number;
  ph?: number;
  is_reactive?: boolean;
  has_free_liquids?: boolean;

  // NORM (pCi/g)
  ra226_pci_g?: number;
  ra228_pci_g?: number;

  // Oil & grease
  oil_grease_mg_kg?: number;
  tph_soil_mg_kg?: number;

  // Plume geometry
  plume_area_sqft?: number;
  plume_length_ft?: number;
  plume_width_ft?: number;
  plume_orientation_degrees?: number;
  plume_status?: string;
  plume_stability?: string;

  // PSH/LNAPL data
  psh_data?: PSHData;

  // Historical plume data
  historical_plume_areas?: Array<{
    date: string;
    area_sqft: number;
    max_concentration_ug_l?: number;
  }>;

  // Hydrogeology
  aquifer_k_ft_day?: number;
  aquifer_porosity?: number;
  hydraulic_gradient?: number;
  flow_direction_degrees?: number;
  depth_to_water_ft?: number;
  aquifer_name?: string;
  aquifer_type?: string;
  soil_type?: string;
  elevation_ft?: number;
  organic_carbon_foc?: number;
  groundwater_velocity_ft_day?: number;
  saturated_thickness_ft?: number;
  water_table_fluctuation_ft?: number;

  // Monitoring wells
  wells?: WellData[];
  total_wells?: number;

  // Remediation
  remediation_active?: boolean;
  remediation_type?: string;
  remediation_start_date?: string;
  remediation_status?: string;

  // Source zone
  source_removed?: boolean;
  source_removal_date?: string;
  estimated_source_mass_lbs?: number;

  // Metadata
  data_source: string;
  data_sources?: string[];
  last_updated?: string;
  gis_layer?: string;
  envirodata_record_id?: string;

  // Status
  found: boolean;
  message?: string;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

export const envirodataApi = {
  /**
   * Get current EnviroData/GIS configuration and model status (API keys masked)
   */
  async getConfig(): Promise<EnviroDataConfigResponse> {
    const response = await apiClient.get<EnviroDataConfigResponse>('/envirodata/config');
    return response.data;
  },

  /**
   * Save both EnviroData and GIS configuration
   */
  async saveConfig(config: IntegrationConfigRequest): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/envirodata/config',
      config
    );
    return response.data;
  },

  /**
   * Save EnviroData API configuration only
   */
  async saveEnviroDataConfig(config: EnviroDataConfig): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/envirodata/config/envirodata',
      config
    );
    return response.data;
  },

  /**
   * Save GIS database configuration
   */
  async saveGISConfig(config: GISConfig): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/envirodata/config/gis',
      config
    );
    return response.data;
  },

  /**
   * Delete all EnviroData/GIS configuration
   */
  async deleteConfig(): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete<{ success: boolean; message: string }>(
      '/envirodata/config'
    );
    return response.data;
  },

  /**
   * Fetch plume data from EnviroData/GIS for the given coordinates
   * Returns all available data to auto-populate the upload form
   */
  async fetchPlumeData(latitude: number, longitude: number): Promise<PlumeDataResponse> {
    const response = await apiClient.post<PlumeDataResponse>(
      '/envirodata/plume-data',
      { latitude, longitude }
    );
    return response.data;
  },

  // ==========================================================================
  // MODEL TRAINING
  // ==========================================================================

  /**
   * Get status of client's trained XGBoost model
   */
  async getModelStatus(): Promise<ModelStatusResponse> {
    const response = await apiClient.get<ModelStatusResponse>('/envirodata/model/status');
    return response.data;
  },

  /**
   * Start training a client-specific XGBoost model
   * Training runs in the background
   */
  async trainModel(request: ModelTrainingRequest): Promise<ModelTrainingResponse> {
    const response = await apiClient.post<ModelTrainingResponse>(
      '/envirodata/model/train',
      request
    );
    return response.data;
  },

  /**
   * Get current training job progress
   */
  async getTrainingStatus(): Promise<TrainingStatusResponse> {
    const response = await apiClient.get<TrainingStatusResponse>('/envirodata/model/training-status');
    return response.data;
  },

  /**
   * Delete client's trained model
   */
  async deleteModel(): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.delete<{ success: boolean; message: string }>(
      '/envirodata/model'
    );
    return response.data;
  },

  // ==========================================================================
  // AUTO-CREATE SITES
  // ==========================================================================

  /**
   * List all sites from client's linked EnviroData/GIS databases
   */
  async listAllSites(): Promise<ListSitesResponse> {
    const response = await apiClient.get<ListSitesResponse>('/envirodata/list-all-sites');
    return response.data;
  },

  /**
   * Bulk-create sites for a product from client's linked databases
   */
  async createSites(request: CreateSitesRequest): Promise<CreateSitesResponse> {
    const response = await apiClient.post<CreateSitesResponse>(
      '/envirodata/create-sites',
      request
    );
    return response.data;
  },
};

// ==========================================================================
// SITE CREATION TYPES
// ==========================================================================

export interface SiteFromDatabase {
  site_id: string;
  site_name?: string;
  latitude: number;
  longitude: number;
  contaminant_type?: string;
  plume_area_sqft?: number;
  max_concentration_ug_l?: number;
  has_wells: boolean;
  well_count: number;
  last_sample_date?: string;
}

export interface ListSitesResponse {
  success: boolean;
  sites: SiteFromDatabase[];
  total_count: number;
  source: string;
  message?: string;
}

export interface CreateSitesRequest {
  product: 'polygon' | 'pathfinder' | 'precept';
  site_ids?: string[];
  enrich_from_toolkits?: boolean;
}

export interface CreatedSite {
  site_id: string;
  site_name: string;
  latitude: number;
  longitude: number;
  product: string;
  status: 'created' | 'updated' | 'error';
  message?: string;
}

export interface CreateSitesResponse {
  success: boolean;
  created_count: number;
  updated_count: number;
  error_count: number;
  sites: CreatedSite[];
  message?: string;
}

export default envirodataApi;
